import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ScreeningEndStatus } from '@estercare/ester-shared'
import { useTransitionFollowUpToStandard } from './ScreeningShared'

type EndConfig = {
  text: string
  buttonLabel?: string
  onButtonClick?: () => void
}

export const useScreeningEndConfig = (endStatus: ScreeningEndStatus, latestScreeningId?: string): EndConfig => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { transitionFollowUpToStandard } = useTransitionFollowUpToStandard()

  const goToBookingRequest = () =>
    navigate('/portal/messages/new', {
      state: { initialMessage: t('employee.messages.bookingRequestTemplate') },
    })

  const endConfigByStatus: Record<ScreeningEndStatus, EndConfig> = {
    [ScreeningEndStatus.FOLLOW_UP_NO_BOOKING]: {
      text: t('employee.submitScreen.endFollowUpNoBookingRequest'),
      buttonLabel: t('employee.submitScreen.endFollowUpNoBookingRequestButton'),
      onButtonClick: () => navigate('/portal/your-health/health-progress'),
    },
    [ScreeningEndStatus.FOLLOW_UP_BOOKING_REQUEST]: {
      text: t('employee.submitScreen.endFollowUpBookingRequest'),
      buttonLabel: t('employee.submitScreen.endBookingRequestButton'),
      onButtonClick: goToBookingRequest,
    },
    [ScreeningEndStatus.FOLLOW_UP_PREGNANCY_ENDED]: {
      text: t('employee.submitScreen.endFollowUpPregnancyEnded'),
      buttonLabel: t('employee.submitScreen.endFollowUpPregnancyEndedButton'),
      onButtonClick: () => {
        if (latestScreeningId) {
          transitionFollowUpToStandard(latestScreeningId)
        }
      },
    },
    [ScreeningEndStatus.CHECK_IN_NO_BOOKING_NO_CALL]: {
      text: t('employee.submitScreen.endCheckInNoBookingNoCall'),
      buttonLabel: t('employee.submitScreen.endCheckInNoBookingNoCallButton'),
      onButtonClick: () => navigate('/portal/health-services/screening/book-health-call'),
    },
    [ScreeningEndStatus.CHECK_IN_NO_BOOKING_HAS_CALL]: {
      text: t('employee.submitScreen.endCheckInNoBookingRequestHasCall'),
      buttonLabel: t('employee.submitScreen.endStartConversationButton'),
      onButtonClick: () => navigate('/portal/messages/new'),
    },
    [ScreeningEndStatus.CHECK_IN_BOOKING_REQUEST]: {
      text: t('employee.submitScreen.endCheckInBookingRequest'),
      buttonLabel: t('employee.submitScreen.endBookingRequestButton'),
      onButtonClick: goToBookingRequest,
    },
    [ScreeningEndStatus.STANDARD_BOOKING_REQUEST]: {
      text: t('employee.submitScreen.endStandardBookingRequest'),
      buttonLabel: t('employee.submitScreen.endBookingRequestButton'),
      onButtonClick: goToBookingRequest,
    },
    [ScreeningEndStatus.DEFAULT]: {
      text: t('employee.submitScreen.followUpProcess'),
    },
  }

  return endConfigByStatus[endStatus] ?? endConfigByStatus[ScreeningEndStatus.DEFAULT]
}
